import { Box, Button, Dialog, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import {
  Badge,
  isNewlyEarnedBadge,
  isNewSession,
  markBadgeAsSeen,
  updateSessionTimestamp,
  UserBadge,
} from '../../../data/badge';

interface BadgeWithId extends Badge {
  id: string;
}

export interface BadgeNotificationProps {
  earnedBadges: Badge[];
  userBadges: UserBadge[];
}

/**
 * Component that watches for newly earned badges and displays a notification modal
 * This component handles the logic for determining when to show badge notifications
 * and prevents showing notifications for badges the user has already seen
 */
export function BadgeNotification({
  earnedBadges,
  userBadges,
}: BadgeNotificationProps) {
  const [open, setOpen] = useState(false);
  const [currentBadge, setCurrentBadge] = useState<BadgeWithId | null>(null);
  const [badgeQueue, setBadgeQueue] = useState<BadgeWithId[]>([]);
  const [isNewAppSession, setIsNewAppSession] = useState(true);

  // On mount, check if this is a new session and update the timestamp
  useEffect(() => {
    const newSession = isNewSession();
    setIsNewAppSession(newSession);
    updateSessionTimestamp();
  }, []);

  // Process earnedBadges when they change or when userBadges load
  useEffect(() => {
    // Early exit when no badges are earned yet
    if (earnedBadges.length === 0 || userBadges.length === 0) {
      return;
    }

    // Find newly earned badges that should be shown
    const newBadgesToShow: BadgeWithId[] = [];

    earnedBadges.forEach(badge => {
      if (isNewlyEarnedBadge(badge, userBadges, isNewAppSession)) {
        // Find the corresponding user badge to get the ID
        const userBadge = userBadges.find(
          ub => ub.badgeType === badge.badgeType
        );
        if (userBadge) {
          newBadgesToShow.push({
            ...badge,
            id: userBadge.id,
          });
        }
      }
    });

    if (newBadgesToShow.length > 0) {
      // Enqueue any new badges to be shown
      setBadgeQueue(prevQueue => [...prevQueue, ...newBadgesToShow]);
    }
  }, [earnedBadges, userBadges, isNewAppSession]);

  // Process the badge queue whenever it changes
  useEffect(() => {
    // If the modal is not open and there are badges in the queue, show the next one
    if (!open && badgeQueue.length > 0) {
      const nextBadge = badgeQueue[0];
      setCurrentBadge(nextBadge);
      setOpen(true);

      // Remove the current badge from the queue
      setBadgeQueue(prevQueue => prevQueue.slice(1));

      // Mark this badge as seen in this browser using its ID
      markBadgeAsSeen(nextBadge.id);
    }
  }, [badgeQueue, open]);

  const handleClose = () => {
    setOpen(false);
    setCurrentBadge(null);
  };

  if (!currentBadge) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4, // Rounded edges
        },
      }}
    >
      <Box sx={{ p: 3, position: 'relative' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 3,
          }}
        >
          <Typography
            variant="h5"
            sx={{ mb: 4, fontWeight: 'bold', textAlign: 'center' }}
          >
            You've earned a badge!
          </Typography>

          <img
            src={currentBadge.imageUrl}
            alt={currentBadge.title}
            style={{ width: '120px', height: '120px', marginBottom: '24px' }}
          />

          <Typography variant="h4" sx={{ mb: 2, textAlign: 'center' }}>
            {currentBadge.title}
          </Typography>

          <Typography variant="body1" sx={{ textAlign: 'center', mb: 4 }}>
            {currentBadge.description}
          </Typography>

          <Button
            variant="contained"
            onClick={handleClose}
            sx={{
              minWidth: '140px',
              bgcolor: theme => theme.interviewAcademy.primaryDark,
              color: 'white',
              '&:hover': {
                bgcolor: theme => theme.interviewAcademy.primaryLight,
              },
              textTransform: 'none',
              px: 3,
              py: 1,
            }}
          >
            Continue
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
